import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { UploadSimple, Lock, LockOpen, FloppyDisk, CheckCircle, ChartBar } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CYCLE = ['P', 'A', 'H', 'CL', 'LOP', 'CO', 'WO'];

const STATUS_STYLES = {
  P:   { bg: '#dcfce7', color: '#166534', border: '#bbf7d0' },
  A:   { bg: '#fee2e2', color: '#991b1b', border: '#fca5a5' },
  H:   { bg: '#fef9c3', color: '#854d0e', border: '#fde68a' },
  CL:  { bg: '#dbeafe', color: '#1e40af', border: '#93c5fd' },
  LOP: { bg: '#fce7f3', color: '#9d174d', border: '#f9a8d4' },
  CO:  { bg: '#d1fae5', color: '#065f46', border: '#6ee7b7' },
  WO:  { bg: '#f3f4f6', color: '#9ca3af', border: '#e5e7eb' },
};

const STATUS_LABEL = {
  P: 'Present (P)',
  A: 'Absent (A)',
  H: 'Holiday (H)',
  CL: 'Casual Leave (CL)',
  LOP: 'Loss of Pay (LOP)',
  CO: 'Comp Off (CO)',
  WO: 'Week Off (WO)',
};

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const todayDefault = () => {
  const d = new Date();
  return { m: d.getMonth() + 1, y: d.getFullYear() };
};

const monthYearToKey = (m, y) => `${y}-${String(m).padStart(2, '0')}`;

const dayLabel = (year, month, dayNumber) => {
  // month is 1-based, dayNumber 1-based
  const d = new Date(year, month - 1, dayNumber);
  return ['S', 'M', 'T', 'W', 'T', 'F', 'S'][d.getDay()];
};

export const AttendancePage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [{ m, y }, setMY] = useState(todayDefault());
  const [rows, setRows] = useState([]);
  const [daysInMonth, setDaysInMonth] = useState(30);
  const [loading, setLoading] = useState(true);
  const [savingMap, setSavingMap] = useState({}); // employee_id -> bool
  const [locked, setLocked] = useState(false);
  const [lockedAt, setLockedAt] = useState(null);
  const [lockedBy, setLockedBy] = useState(null);
  const [readyForPayroll, setReadyForPayroll] = useState(false);
  const [payrollMarkedBy, setPayrollMarkedBy] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);

  const monthKey = useMemo(() => monthYearToKey(m, y), [m, y]);
  // Once Ready for Payroll is set, NO ONE can edit (even admin) — admin must unlock first.
  const canEdit = !readyForPayroll && (isAdmin || !locked);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/attendance/${monthKey}`, { withCredentials: true });
        if (cancelled) return;
        setDaysInMonth(res.data.days_in_month);
        setRows(res.data.rows || []);
        setLocked(!!res.data.locked);
        setLockedAt(res.data.locked_at);
        setLockedBy(res.data.locked_by);
        setReadyForPayroll(!!res.data.ready_for_payroll);
        setPayrollMarkedBy(res.data.payroll_marked_by);
      } catch (err) {
        console.error('attendance load', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [monthKey]);

  const cycleCell = async (employee_id, idx) => {
    if (!canEdit) return; // locked & not admin
    const row = rows.find((r) => r.employee_id === employee_id);
    if (!row) return;
    const current = row.days[idx];
    const nextIdx = (STATUS_CYCLE.indexOf(current) + 1) % STATUS_CYCLE.length;
    const next = STATUS_CYCLE[nextIdx];

    // optimistic update
    setRows((prev) =>
      prev.map((r) =>
        r.employee_id === employee_id
          ? { ...r, days: r.days.map((d, i) => (i === idx ? next : d)) }
          : r
      )
    );
    setSavingMap((s) => ({ ...s, [employee_id]: true }));
    try {
      await axios.post(
        `${API_URL}/api/attendance/${monthKey}/cell`,
        { employee_id, day_index: idx, status: next },
        { withCredentials: true }
      );
    } catch (err) {
      console.error('save cell', err);
      // revert
      setRows((prev) =>
        prev.map((r) =>
          r.employee_id === employee_id
            ? { ...r, days: r.days.map((d, i) => (i === idx ? current : d)) }
            : r
        )
      );
      if (err.response?.status === 403) {
        alert(err.response.data?.detail || 'Locked — cannot edit');
        setLocked(true);
      }
    } finally {
      setSavingMap((s) => ({ ...s, [employee_id]: false }));
    }
  };

  const lockMonth = async () => {
    const monthLabel = `${MONTHS[m - 1]} ${y}`;
    if (!window.confirm(
      isAdmin
        ? `Lock ${monthLabel} attendance?\nAttendance staff will no longer be able to edit it.`
        : `Save & Lock ${monthLabel} attendance?\nYou will NOT be able to edit it again — only admin can make changes after lock.\n\nMake sure all days are correctly marked.`
    )) return;
    setLockBusy(true);
    try {
      const res = await axios.post(`${API_URL}/api/attendance/${monthKey}/lock`, {}, { withCredentials: true });
      setLocked(true);
      setLockedAt(res.data.locked_at);
      setLockedBy(user?.name || user?.email);
    } catch (err) {
      alert('Failed to lock: ' + (err.response?.data?.detail || err.message));
    } finally { setLockBusy(false); }
  };

  const markReadyForPayroll = async () => {
    const monthLabel = `${MONTHS[m - 1]} ${y}`;
    if (!window.confirm(
      `Mark ${monthLabel} attendance as READY FOR PAYROLL?\n\n` +
      `This will:\n` +
      `  • Lock the attendance permanently for staff (only admin can edit)\n` +
      `  • Signal that the data is finalized for salary processing\n\n` +
      `You can still unlock it later if needed.`
    )) return;
    setLockBusy(true);
    try {
      await axios.post(`${API_URL}/api/attendance/${monthKey}/lock`, { ready_for_payroll: true }, { withCredentials: true });
      setLocked(true);
      setReadyForPayroll(true);
      setLockedBy(user?.name || user?.email);
      setPayrollMarkedBy(user?.name || user?.email);
    } catch (err) {
      alert('Failed: ' + (err.response?.data?.detail || err.message));
    } finally { setLockBusy(false); }
  };

  const unlockMonth = async () => {
    if (!window.confirm(`Unlock ${MONTHS[m - 1]} ${y}? Attendance staff will be able to edit it again. Payroll readiness will also be cleared.`)) return;
    setLockBusy(true);
    try {
      await axios.post(`${API_URL}/api/attendance/${monthKey}/unlock`, {}, { withCredentials: true });
      setLocked(false);
      setReadyForPayroll(false);
    } catch (err) {
      alert('Failed to unlock: ' + (err.response?.data?.detail || err.message));
    } finally { setLockBusy(false); }
  };

  const presentCount = (days) => days.filter((d) => d === 'P').length;

  return (
    <div data-testid="attendance-page">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--dx-text)' }}>
            Attendance Entry
          </h1>
          <p className="text-xs mt-1" style={{ color: 'var(--dx-text2)' }}>
            Click any cell to cycle status. Changes auto-save. Click <strong>Save &amp; Lock</strong> when done — locked months can only be edited by admin.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="attendance-month"
            value={m}
            onChange={(e) => setMY({ m: +e.target.value, y })}
            className="px-3 py-1.5 text-sm border rounded-md bg-white"
            style={{ borderColor: 'var(--dx-border2)' }}
          >
            {MONTHS.map((mn, i) => (
              <option key={mn} value={i + 1}>{mn}</option>
            ))}
          </select>
          <select
            data-testid="attendance-year"
            value={y}
            onChange={(e) => setMY({ m, y: +e.target.value })}
            className="px-3 py-1.5 text-sm border rounded-md bg-white"
            style={{ borderColor: 'var(--dx-border2)' }}
          >
            {[2024, 2025, 2026, 2027].map((yr) => (
              <option key={yr} value={yr}>{yr}</option>
            ))}
          </select>
          <Link
            to="/import-attendance"
            data-testid="import-csv-link"
            className="px-3 py-1.5 text-sm border rounded-md flex items-center gap-1.5 bg-white hover:bg-gray-50"
            style={{ borderColor: 'var(--dx-border2)', color: 'var(--dx-text)' }}
          >
            <UploadSimple size={14} /> Import CSV
          </Link>
          {isAdmin && (
            <Link
              to="/attendance-report"
              data-testid="attendance-report-link"
              className="px-3 py-1.5 text-sm border rounded-md flex items-center gap-1.5 bg-white hover:bg-gray-50"
              style={{ borderColor: 'var(--dx-border2)', color: 'var(--dx-text)' }}
            >
              <ChartBar size={14} /> Reports
            </Link>
          )}
          {!locked ? (
            <button
              onClick={lockMonth}
              disabled={lockBusy}
              data-testid="lock-month-btn"
              className="px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 text-white"
              style={{ background: isAdmin ? '#0ea5e9' : '#16a34a', opacity: lockBusy ? 0.6 : 1 }}
              title={isAdmin ? 'Lock this month so staff can no longer edit' : 'Save & Lock — you will not be able to edit again'}
            >
              <FloppyDisk size={14} /> {isAdmin ? 'Lock Month' : 'Save & Lock'}
            </button>
          ) : isAdmin ? (
            <button
              onClick={unlockMonth}
              disabled={lockBusy}
              data-testid="unlock-month-btn"
              className="px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 text-white"
              style={{ background: '#f59e0b', opacity: lockBusy ? 0.6 : 1 }}
            >
              <LockOpen size={14} /> Unlock
            </button>
          ) : null}
          {isAdmin && !readyForPayroll && (
            <button
              onClick={markReadyForPayroll}
              disabled={lockBusy}
              data-testid="ready-payroll-btn"
              className="px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 text-white"
              style={{ background: '#16a34a', opacity: lockBusy ? 0.6 : 1 }}
              title="Lock and signal that this month's attendance is finalized for payroll processing"
            >
              <CheckCircle size={14} weight="fill" /> Ready for Payroll
            </button>
          )}
        </div>
      </div>

      {/* Lock banner */}
      {locked && (
        <div
          className="mb-4 px-4 py-2.5 rounded-md flex items-center gap-2 text-sm"
          style={{
            background: readyForPayroll ? '#dcfce7' : '#fef3c7',
            borderLeft: `4px solid ${readyForPayroll ? '#16a34a' : '#d97706'}`,
            color: readyForPayroll ? '#14532d' : '#78350f',
          }}
          data-testid="lock-banner"
        >
          {readyForPayroll ? <CheckCircle size={16} weight="fill" /> : <Lock size={16} weight="fill" />}
          <span>
            {readyForPayroll ? (
              <>
                <strong>{MONTHS[m - 1]} {y} is READY FOR PAYROLL.</strong>{' '}
                {payrollMarkedBy && `Marked by ${payrollMarkedBy}${lockedAt ? ` on ${new Date(lockedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}. `}
                {isAdmin
                  ? 'You can proceed to generate payroll on the Payroll page.'
                  : 'Admin has finalized attendance. No edits allowed.'}
              </>
            ) : (
              <>
                <strong>{MONTHS[m - 1]} {y} is locked.</strong>{' '}
                {lockedBy && `Locked by ${lockedBy}${lockedAt ? ` on ${new Date(lockedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : ''}. `}
                {isAdmin ? 'You can still edit. Use "Unlock" to allow staff edits again.' : 'Only admin can make changes now.'}
              </>
            )}
          </span>
        </div>
      )}

      {/* Legend */}
      <div
        className="p-3 mb-4 flex flex-wrap gap-x-4 gap-y-2 items-center text-[11px]"
        style={{ background: 'var(--dx-surface)', border: '1px solid var(--dx-border)', borderRadius: 8 }}
        data-testid="attendance-legend"
      >
        {STATUS_CYCLE.map((s) => (
          <div key={s} className="flex items-center gap-1.5" style={{ color: 'var(--dx-text2)' }}>
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ background: STATUS_STYLES[s].bg, border: `1px solid ${STATUS_STYLES[s].border}` }}
            />
            <span>{STATUS_LABEL[s]}</span>
          </div>
        ))}
        <span className="italic" style={{ color: 'var(--dx-text3)' }}>— click any cell to cycle status</span>
      </div>

      {loading ? (
        <div className="text-sm" style={{ color: 'var(--dx-text2)' }}>Loading attendance grid…</div>
      ) : rows.length === 0 ? (
        <div className="text-sm" style={{ color: 'var(--dx-text2)' }}>
          No employees found. Add employees first to mark attendance.
        </div>
      ) : (
        <div className="space-y-3" data-testid="attendance-rows">
          {rows.map((r) => {
            const present = presentCount(r.days);
            return (
              <div
                key={r.employee_id}
                className="dx-card overflow-hidden"
                data-testid={`attendance-row-${r.employee_id}`}
              >
                <div
                  className="px-4 py-2.5 flex items-center justify-between"
                  style={{ background: 'var(--dx-bg)', borderBottom: '1px solid var(--dx-border)' }}
                >
                  <div>
                    <span className="text-[13px] font-semibold" style={{ color: 'var(--dx-text)' }}>
                      {r.name}
                    </span>
                    {r.emp_id && (
                      <span className="text-[11px] ml-2" style={{ color: 'var(--dx-text3)' }}>
                        {r.emp_id}
                      </span>
                    )}
                  </div>
                  <span
                    className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: STATUS_STYLES.P.bg, color: STATUS_STYLES.P.color }}
                  >
                    P: {present}
                    {savingMap[r.employee_id] ? ' • saving…' : ''}
                  </span>
                </div>
                <div className="overflow-x-auto px-3 py-3">
                  <div className="flex gap-1" style={{ minWidth: 'max-content' }}>
                    {r.days.map((status, i) => {
                      const dn = i + 1;
                      const style = STATUS_STYLES[status] || STATUS_STYLES.P;
                      return (
                        <div key={i} className="flex flex-col items-center" style={{ width: 38 }}>
                          <div className="text-[9px] font-bold uppercase" style={{ color: 'var(--dx-text3)' }}>
                            {dayLabel(y, m, dn)}
                          </div>
                          <div className="text-[10px] mb-1" style={{ color: 'var(--dx-text3)' }}>{dn}</div>
                          <button
                            type="button"
                            onClick={() => cycleCell(r.employee_id, i)}
                            disabled={!canEdit}
                            data-testid={`att-cell-${r.employee_id}-${i}`}
                            className="select-none"
                            style={{
                              width: 32,
                              height: 26,
                              borderRadius: 4,
                              background: style.bg,
                              color: style.color,
                              border: `1px solid ${style.border}`,
                              fontSize: 10,
                              fontWeight: 700,
                              cursor: canEdit ? 'pointer' : 'not-allowed',
                              opacity: canEdit ? 1 : 0.55,
                              transition: 'transform 0.12s',
                            }}
                            title={canEdit ? STATUS_LABEL[status] : 'Locked — only admin can edit'}
                            onMouseEnter={(e) => { if (canEdit) e.currentTarget.style.transform = 'scale(1.12)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                          >
                            {status}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AttendancePage;
